import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clients as clientsApi, brands as brandsApi, settings as settingsApi, leads as leadsApi } from '@/utils/api';
import { Plus, Search, Building2, User, ExternalLink, ChevronRight, X, Ban, Check, Link2, KeyRound, Copy } from 'lucide-react';
import { toast } from 'sonner';

function ClientCard({ client, onClick, onBlock, onUnblock, onPortalUrl, onResetPassword }) {
  const isBlocked = client.status === 'blocked';
  const stopAnd = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div
      className="card-interactive p-4 flex items-start justify-between"
      onClick={onClick}
      data-testid={`client-card-${client.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-btn flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
          style={{ background: client.brand_color || '#006080', opacity: isBlocked ? 0.4 : 1 }}
        >
          {client.company_name?.charAt(0) || 'C'}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{client.company_name}</p>
            {isBlocked && <span className="badge-status" style={{ background: '#B34A3A15', color: '#B34A3A' }}>BLOCKED</span>}
          </div>
          <p className="text-xs" style={{ color: 'var(--muted)' }}>{client.contact_name}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{client.email}</p>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {client.brand_name && (
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ background: `${client.brand_color || '#006080'}15`, color: client.brand_color || '#006080' }}
          >
            {client.brand_name}
          </span>
        )}
        <div className="flex items-center gap-1 mt-1 flex-wrap justify-end">
          <button type="button" onClick={stopAnd(onPortalUrl)} className="text-xs px-2 py-1 rounded-btn flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} data-testid={`portal-url-${client.id}`} title="Copy portal URL">
            <Link2 size={10} /> URL
          </button>
          <button type="button" onClick={stopAnd(onResetPassword)} className="text-xs px-2 py-1 rounded-btn flex items-center gap-1" style={{ background: '#D9A44115', color: '#D9A441' }} data-testid={`reset-password-${client.id}`} title="Reset password">
            <KeyRound size={10} /> Reset pw
          </button>
          {isBlocked ? (
            <button type="button" onClick={stopAnd(onUnblock)} className="text-xs px-2 py-1 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#3C8A5A15', color: '#3C8A5A' }} data-testid={`unblock-${client.id}`}>
              <Check size={10} /> Unblock
            </button>
          ) : (
            <button type="button" onClick={stopAnd(onBlock)} className="text-xs px-2 py-1 rounded-btn flex items-center gap-1" style={{ color: '#B34A3A', border: '1px solid #B34A3A40' }} data-testid={`block-${client.id}`}>
              <Ban size={10} /> Block
            </button>
          )}
        </div>
        <ChevronRight size={16} style={{ color: 'var(--muted)' }} />
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '',
    brand_id: '', website: '', address: '', initial_password: 'Client@123'
  });

  useEffect(() => {
    Promise.all([
      clientsApi.list(),
      brandsApi.list(),
      settingsApi.projectTypes(),
    ]).then(([c, b, pt]) => {
      setClients(c.data);
      setBrands(b.data);
      setProjectTypes(pt.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = clients.filter(c =>
    (!search || c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())) &&
    (!brandFilter || c.brand_id === brandFilter)
  );

  const reloadClients = () => clientsApi.list().then(r => setClients(r.data)).catch(() => {});
  const handleBlock = async (id) => {
    if (!window.confirm('Block this client? They will be logged out and unable to sign in.')) return;
    try { await leadsApi.blockClient(id); toast.success('Client blocked'); reloadClients(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleUnblock = async (id) => {
    try { await leadsApi.unblockClient(id); toast.success('Client unblocked'); reloadClients(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handlePortalUrl = async (id) => {
    try {
      const r = await clientsApi.portalUrl(id);
      const text = `Sign in to your portal:\n${r.data.login_url}\n\nLogin email: ${r.data.email}`;
      await navigator.clipboard.writeText(text);
      toast.success('Portal URL + email copied to clipboard');
    } catch { toast.error('Failed'); }
  };
  const handleResetPassword = async (id) => {
    if (!window.confirm("Generate a new temp password? The client's current password will stop working immediately.")) return;
    try {
      const r = await clientsApi.resetPassword(id);
      const msg = `New temp password for ${r.data.email}:\n\n${r.data.temp_password}\n\nCopied to clipboard. Share securely — it is shown only once.`;
      await navigator.clipboard.writeText(r.data.temp_password);
      window.alert(msg);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await clientsApi.create({ ...form, create_user_account: true });
      setClients(prev => [r.data, ...prev]);
      setShowCreate(false);
      toast.success('Client created');
      navigate(`/admin/clients/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create client');
    }
  };

  return (
    <div className="p-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Clients</h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>{clients.length} client{clients.length !== 1 ? 's' : ''} across all brands</p>
        </div>
        <button
          className="btn-primary flex items-center gap-2 text-sm"
          onClick={() => setShowCreate(true)}
          data-testid="add-client-button"
        >
          <Plus size={16} />
          Add Client
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
          <input
            className="input-agency pl-8 text-sm"
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="client-search-input"
          />
        </div>
        <select
          className="input-agency w-40 text-sm"
          value={brandFilter}
          onChange={e => setBrandFilter(e.target.value)}
          data-testid="brand-filter-select"
        >
          <option value="">All brands</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* Client grid */}
      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card-base p-4 h-24 animate-pulse" style={{ background: 'var(--border)' }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <Building2 size={40} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="font-semibold" style={{ color: 'var(--ink)' }}>No clients found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            {search ? 'Try adjusting your search' : 'Add your first client to get started'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="clients-grid">
          {filtered.map(c => (
            <ClientCard key={c.id} client={c} onClick={() => navigate(`/admin/clients/${c.id}`)} onBlock={() => handleBlock(c.id)} onUnblock={() => handleUnblock(c.id)} onPortalUrl={() => handlePortalUrl(c.id)} onResetPassword={() => handleResetPassword(c.id)} />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-lg p-6 animate-fade-in overflow-y-auto max-h-[90vh]" data-testid="create-client-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-black" style={{ color: 'var(--ink)' }}>New Client</h2>
              <button onClick={() => setShowCreate(false)} style={{ color: 'var(--muted)' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Company Name *</label>
                  <input className="input-agency text-sm" required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="client-company-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Contact Name *</label>
                  <input className="input-agency text-sm" required value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} data-testid="client-contact-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Email *</label>
                  <input className="input-agency text-sm" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="client-email-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Phone</label>
                  <input className="input-agency text-sm" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Brand *</label>
                <select className="input-agency text-sm" required value={form.brand_id} onChange={e => setForm({...form, brand_id: e.target.value})} data-testid="client-brand-select">
                  <option value="">Select brand...</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Initial Password</label>
                <input className="input-agency text-sm font-mono" value={form.initial_password} onChange={e => setForm({...form, initial_password: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Website</label>
                <input className="input-agency text-sm" type="url" placeholder="https://" value={form.website} onChange={e => setForm({...form, website: e.target.value})} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="flex-1 btn-primary py-2 text-sm" data-testid="create-client-submit">Create Client</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
